import React, { useRef, useEffect, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import SvgIcon from '@material-ui/core/SvgIcon';
import SendIcon from '@material-ui/icons/Send';
import SentimentSatisfiedIcon from '@material-ui/icons/SentimentSatisfied';
import { Picker } from 'emoji-mart';
import '!style-loader!css-loader!emoji-mart/css/emoji-mart.css';

import ChatStore from 'stores/ChatStore';
import FirebaseStore from 'stores/FirebaseStore';
import UserStore from 'stores/UserStore';
import GiphyStore from 'stores/GiphyStore';
import GiphyDesktop from 'components/Giphy/GiphyDesktop/GiphyDesktop';
import GiphyMobile from 'components/Giphy/GiphyMobile/GiphyMobile';
import { VIDEO_AND_CHAT_CLASS } from 'enum/videoPlayer';
import allUserGroups from 'enum/userGroups';
import { generateID } from 'utils/helpers';

import styles from './ChatInputHandlers.css';


const IDS = {
  prefix: 'dzin56o1',
  element: 'chatInputHandler',
  chatEmojiButton: 'chatEmojiButton',
  chatMessageButton: 'chatMessageButton',
  gifButton: 'gifButton',
};
const makeId = generateID(IDS.prefix, IDS.element);

const emojiPickerPositionProps = {
  vertical: 'bottom',
  horizontal: 'right',
};

const MBA_MAX_VERSION = 6309;

const ChatInputHandlers = (props) => {
  const {
    canProceedWithAction,
    inputId,
    isDesktop,
    isGroupShowChat,
    isPrivateChat,
    message,
    recipient,
    roomName,
    user: {
      accessHash,
      accessLevel,
      username,
    },
  } = props;

  const {
    activeConversation,
    addEmoji,
    banUserFromChatIfBroadcaster,
    isEmojiPickerVisible,
    onCloseEmojiPicker,
    openEmojiPicker,
    sendGroupShowMessage,
    sendMainChatMessage,
  } = ChatStore;

  const { mbaVersion } = FirebaseStore;

  const {
    closeDrawer,
    isGifsWrapperVisible,
    openGifsAndSetAnchor,
    toggleMobileDrawer,
  } = GiphyStore;

  const emojiPickerRef = useRef();

  const dontShowGiphyButton = mbaVersion !== 0 && mbaVersion !== 100 && mbaVersion < MBA_MAX_VERSION;

  const MOBILE_WIDTH_START = 897;
  const updateWidth = () => {
    if (window.innerWidth < MOBILE_WIDTH_START) {
      GiphyStore.closePopper();
    }
  };

  useEffect(() => {
    window.addEventListener('resize', updateWidth);

    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const gifsInChat = UserStore.hasCF(allUserGroups.GIFS_IN_PLAYER_CHAT) && !dontShowGiphyButton;

  const onSendMessage = useCallback((newMessage, isGif, gifUrlAnalytics) => {
    if (banUserFromChatIfBroadcaster(username, accessLevel, roomName)) {
      return;
    }

    if (isGroupShowChat) {
      sendGroupShowMessage(
        username,
        accessHash,
        roomName,
        newMessage,
        isGif,
        gifUrlAnalytics
      );
    } else {
      sendMainChatMessage(
        username,
        accessHash,
        roomName,
        newMessage,
        recipient,
        isGif,
        gifUrlAnalytics
      );
    }
  }, [
    accessHash,
    accessLevel,
    isGroupShowChat,
    recipient,
    roomName,
    username,
    sendGroupShowMessage,
    sendMainChatMessage,
    banUserFromChatIfBroadcaster,
  ]);

  const onSubmitMessage = useCallback(() => {
    onSendMessage(message, false);
  }, [message, onSendMessage]);

  const onOpenEmojiOverlay = useCallback((e) => {
    if (canProceedWithAction()) {
      openEmojiPicker();
    } else {
      e?.preventDefault();
    }
  }, [canProceedWithAction, openEmojiPicker]);

  const onCloseEmojiOverlay = useCallback(() => {
    onCloseEmojiPicker();
    setTimeout(() => document.getElementById(inputId).focus(), 0);
  }, [inputId, onCloseEmojiPicker]);

  const onSendGift = useCallback((e) => {
    if (!canProceedWithAction()) {
      e?.preventDefault();

      return;
    }

    if (isDesktop) {
      openGifsAndSetAnchor(e);
    } else {
      toggleMobileDrawer();
    }
  }, [
    canProceedWithAction,
    isDesktop,
    openGifsAndSetAnchor,
    toggleMobileDrawer,
  ]);

  const containerClasses = classNames({
    [styles.actionWrappers]: true,
    [styles.actionWrappersPrivate]: isPrivateChat || (activeConversation && !isDesktop),
  });

  const videoAndChatWrapper = document.querySelector(`.${VIDEO_AND_CHAT_CLASS}`);

  useEffect(() => {
    closeDrawer();
  }, [closeDrawer]);

  return (
    <div className={containerClasses}>
      <div className={styles.anchor} />
      {gifsInChat && (
        <Fragment>
          <IconButton
            aria-label="send gift"
            classes={{ root: styles.iconButton }}
            disableRipple={true}
            id={makeId(IDS.gifButton)}
            onClick={onSendGift}
          >
            <SvgIcon className={styles.menuIcons}>
              {/* eslint-disable-next-line max-len */}
              <path d="M19,3H5C3.9,3,3,3.9,3,5v14c0,1.1,0.9,2,2,2h14c1.1,0,2-0.9,2-2V5C21,3.9,20.1,3,19,3z M9.5,13v-1h1v1c0,0.55-0.45,1-1,1h-1 c-0.55,0-1-0.45-1-1v-2c0-0.55,0.45-1,1-1h1c0.55,0,1,0.45,1,1h-2v2H9.5z M12.5,14h-1v-4h1V14z M16.5,11h-2v0.5H16v1h-1.5V14h-1v-4 h3V11z" />
            </SvgIcon>
          </IconButton>
          {isGifsWrapperVisible && isDesktop && !dontShowGiphyButton && (
            <GiphyDesktop
              handleSendButton={onSendMessage}
              isDesktop={isDesktop}
            />
          )}
          {!isDesktop && !dontShowGiphyButton && (
            <GiphyMobile handleSendButton={onSendMessage} />
          )}
        </Fragment>
      )}

      <IconButton
        aria-label="add emoji"
        classes={{ root: styles.iconButton }}
        disableRipple={true}
        id={makeId(IDS.chatEmojiButton)}
        onClick={onOpenEmojiOverlay}
      >
        <SentimentSatisfiedIcon />
      </IconButton>

      <Popover
        anchorEl={emojiPickerRef.current}
        anchorOrigin={emojiPickerPositionProps}
        container={videoAndChatWrapper}
        open={isEmojiPickerVisible}
        transformOrigin={emojiPickerPositionProps}
        onClose={onCloseEmojiOverlay}
      >
        <Picker
          set="messenger"
          onSelect={addEmoji}
        />
      </Popover>

      <IconButton
        aria-label="send message"
        classes={{ root: styles.sendButton }}
        disableRipple={true}
        disabled={!message?.length}
        id={makeId(IDS.chatMessageButton)}
        onClick={onSubmitMessage}
      >
        <SendIcon />
      </IconButton>
    </div>
  );
};

ChatInputHandlers.propTypes = {
  canProceedWithAction: PropTypes.func.isRequired,
  inputId: PropTypes.string.isRequired,
  isDesktop: PropTypes.bool,
  isGroupShowChat: PropTypes.bool,
  isPrivateChat: PropTypes.bool,
  message: PropTypes.string,
  recipient: PropTypes.string,
  roomName: PropTypes.string,
  user: PropTypes.object,
};

ChatInputHandlers.defaultProps = {
  isDesktop: false,
  isGroupShowChat: false,
  isPrivateChat: false,
  message: undefined,
  recipient: undefined,
  roomName: undefined,
  user: undefined,
};

export default observer(ChatInputHandlers);
